import type { AuthenticatedUser } from '@puffin/app-frame-api';
import { AuthenticationResult, Configuration, EventMessage, EventType, InteractionType, PopupRequest, PublicClientApplication } from '@azure/msal-browser';
import type { AccountInfo } from '@azure/msal-browser';
import { MsalAuthenticationTemplate, MsalProvider, useMsal } from '@azure/msal-react';
import React, { useEffect, useState } from 'react';
import { AuthenticatedUserContext } from './AuthenticatedUserContext';
import { getUserFromAccessToken } from './getUserFromAccessToken';

const API_TOKEN_SESSION_STORAGE = 'mp.dev.platform.token';
const tenantId = '3823f06b-6835-468e-b04b-8ea04b6585ba';
const clientId = 'c747e4e5-cbf7-4e47-af21-5231e147f3fa';
const loginRequest: PopupRequest = {
  scopes: ['https://meisterplan.dev/.default'],
};
const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}/v2.0`,
    redirectUri: '/',
  },
};
const msalInstance = new PublicClientApplication(msalConfig);

const getCurrentAccount = (accounts = msalInstance.getAllAccounts({ tenantId })): AccountInfo | undefined => {
  return msalInstance.getActiveAccount() ?? accounts.at(0);
};

const ensureActiveAccount = (): AccountInfo | undefined => {
  const activeAccount = msalInstance.getActiveAccount();
  if (activeAccount) {
    return activeAccount;
  }

  const account = getCurrentAccount();
  if (account) {
    msalInstance.setActiveAccount(account);
  }
  return account;
};

const initializeAuth = async () => {
  await msalInstance.initialize();

  ensureActiveAccount();

  msalInstance.addEventCallback((event: EventMessage) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as AuthenticationResult;
      const account = payload.account;
      if (account.tenantId !== tenantId) {
        return;
      }
      msalInstance.setActiveAccount(account);
    }
  });
};

const getAccessTokenFromSessionStorage = (): string | null => {
  return sessionStorage.getItem(API_TOKEN_SESSION_STORAGE);
};

const getAccessTokenForAccount = async (account: AccountInfo | null | undefined): Promise<string | undefined> => {
  if (!account) {
    return undefined;
  }
  const response = await msalInstance.acquireTokenSilent({
    ...loginRequest,
    account,
  });
  return response.accessToken;
};

export const getAccessToken = async (): Promise<string | undefined> => {
  return getAccessTokenFromSessionStorage() ?? getAccessTokenForAccount(ensureActiveAccount());
};

export const getUser = async (): Promise<AuthenticatedUser | undefined> => {
  const accessToken = await getAccessToken();
  return accessToken ? getUserFromAccessToken(accessToken) : undefined;
};

const AuthenticatedUserArea: React.FunctionComponent<{ children: React.ReactNode }> = ({ children }) => {
  const { accounts } = useMsal();
  const account = getCurrentAccount(accounts);
  const homeAccountId = account?.homeAccountId;
  const [user, setUser] = useState<AuthenticatedUser>();

  useEffect(() => {
    let active = true;
    const currentAccount = homeAccountId ? msalInstance.getAccount({ homeAccountId }) : undefined;
    void getAccessTokenForAccount(currentAccount)
      .then((accessToken) => {
        if (active) {
          setUser(accessToken ? getUserFromAccessToken(accessToken) : undefined);
        }
      })
      .catch(() => {
        if (active) {
          setUser(undefined);
        }
      });
    return () => {
      active = false;
    };
  }, [homeAccountId]);

  return <AuthenticatedUserContext.Provider value={user}>{children}</AuthenticatedUserContext.Provider>;
};

const MsalAuthorizedArea: React.FunctionComponent<{ children: React.ReactNode }> = (props) => {
  const [authInitialized, setAuthInitialized] = React.useState(false);

  useEffect(() => {
    void initializeAuth().then(() => setAuthInitialized(true));
  }, []);

  if (!authInitialized) {
    return null;
  }
  return (
    <MsalProvider instance={msalInstance}>
      <MsalAuthenticationTemplate authenticationRequest={loginRequest} interactionType={InteractionType.Redirect}>
        <AuthenticatedUserArea>{props.children}</AuthenticatedUserArea>
      </MsalAuthenticationTemplate>
    </MsalProvider>
  );
};

export const AuthorizedArea: React.FunctionComponent<{ children: React.ReactNode }> = (props) => {
  const accessToken = getAccessTokenFromSessionStorage();
  if (accessToken) {
    return <AuthenticatedUserContext.Provider value={getUserFromAccessToken(accessToken)}>{props.children}</AuthenticatedUserContext.Provider>;
  }
  return <MsalAuthorizedArea>{props.children}</MsalAuthorizedArea>;
};
